import {
  KeyboardEvent,
  ChangeEvent,
  Fragment,
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import {
  CircularProgress,
  ClickAwayListener,
  Divider,
  Grow, IconButton,
  ListItemIcon,
  Paper,
  Popper,
  TextField
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { IFilterResult, IFilterResultGroup, IQueryController } from "../../types";
import WidgetsIcon from "@mui/icons-material/Widgets";
import QueryDialog from "../dialogs/queryDialog";
import { SystemContext } from "../../context/systemContext";
import MainModule from "../../modules/mainModule";
import { IRequest, useLazyFetch } from "../../system/useFetch";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary } from "../accordion";

interface ISearchBoxComponentProps {
  mobile: boolean;
  onExpanded: (isExpanded: boolean) => void;
}

interface IQueryData {
  // code: string;
  request: IRequest;
  onDataArrived: (data: any) => ReactElement;
}


// const Accordion = withStyles({
//   root: {
//     border: "1px solid rgba(0, 0, 0, .125)",
//     boxShadow: "none",
//     "&:not(:last-child)": {
//       borderBottom: 0,
//     },
//     "&:before": {
//       display: "none",
//     },
//     "&$expanded": {
//       margin: "auto",
//     },
//   },
//   expanded: {},
// })(MuiAccordion);

// const AccordionSummary = withStyles({
//   root: {
//     backgroundColor: "rgba(0, 0, 0, .03)",
//     borderBottom: "1px solid rgba(0, 0, 0, .125)",
//     marginBottom: -1,
//     minHeight: 56,
//     "&$expanded": {
//       minHeight: 56,
//     },
//   },
//   content: {
//     "&$expanded": {
//       margin: "12px 0",
//     },
//   },
//   expanded: {},
// })(MuiAccordionSummary);
//
// const AccordionDetails = withStyles(() => ({
//   root: {
//     padding: 0 //theme.spacing(2),
//   },
// }))(MuiAccordionDetails);

const SearchBoxComponent = ({ mobile, onExpanded }: ISearchBoxComponentProps): ReactElement => {
  const systemContext = useContext(SystemContext);
  const [getData, { loading, error, data }] = useLazyFetch<IFilterResultGroup[]>(true);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  const [filter, setFilter] = useState<string>("");
  const [currentModule, setCurrentModule] = useState<IQueryData | null>(null);
  const [expanded, setExpanded] = useState<string | false>("panel1");

  useEffect(() => {
    if (data) {
      setOpen(true);
    }
  }, [data]);


  const onCollapse = () => {
    setShowTextInput(false);
    onExpanded(false);
  };

  const onEnterKey = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && filter !== "") {
      setCurrentModule(null);

      onCollapse();
      setExpanded(false);
      await getData({ module: "filter", query: "getAllByFilter", payload: { filter } });
    }
  };

  const onSelectedItem = (_group: IFilterResultGroup, item: IFilterResult) => {
    MainModule.getController<IQueryController>(item.context[0].module)
      .then(controller => {
        if (controller) {
          setCurrentModule({
            // code: group.module,
            request: { module: "production", query: "getInformation", payload: { context: item.context } },
            onDataArrived: (dataArrived: any) => controller.onDataArrived(dataArrived)
          });
          setOpen(false);
          setFilter("");
        }
      });
  };

  const result = !loading && data ? data : [];

  const onExpandClick = () => {
    setShowTextInput(true);
    onExpanded(true);
  };

  const clearFilter = () => {
    setOpen(false);
    setFilter("");
  };

  const handleChange = (panel: string) => {
    setExpanded(panel);
  };

  const onchangeFilter = (event: ChangeEvent<HTMLInputElement>) => setFilter(event.target.value);
  const onClosed = () => setCurrentModule(null);

  return (
    <div ref={anchorRef}>

      {!mobile || showTextInput
        ? <TextField
          id="outlined-size-small"
          variant="outlined"
          size="small"
          placeholder={systemContext.language.search}
          value={filter}
          onKeyUp={onEnterKey}
          onChange={onchangeFilter}
          onBlur={onCollapse}
          slotProps={{ input: {
              startAdornment:
              <InputAdornment position="start">
                {loading ? <CircularProgress size={"1.5em"}/> : <SearchIcon/>}
              </InputAdornment>
          }}}
        />
        : <IconButton onClick={onExpandClick}>
          <SearchIcon/>
        </IconButton>
      }
      <Popper open={open} anchorEl={anchorRef.current} role={undefined} transition={true} disablePortal={true}>
        {({ TransitionProps, placement }) =>
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === "bottom" ? "center top" : "center bottom",
              width: "500px",
              maxHeight: "650px"
            }}>
            <Paper>
              <ClickAwayListener onClickAway={clearFilter}>
                <div>
                  { result.length === 0 && !error &&
                    <List component="nav" sx={{ maxHeight: 500, overflowY: "auto" }}>
                      <ListItem>
                        <ListItemText primary="No results"/>
                      </ListItem>
                    </List>
                  }

                  {result.map((group: IFilterResultGroup, groupIndex: number) => {
                    const groupKey = `group-search-${groupIndex}`;

                    return (
                      <Accordion key={groupKey} square={true} expanded={expanded === groupKey} onChange={() => handleChange(groupKey)}>
                        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header"  expandIcon={<ExpandMoreIcon />}>
                          <strong>{group.title}</strong>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List component="nav" sx={{ maxHeight: 500, overflowY: "auto" }} style={{ width: "100%" }}>
                            {group.items.map((item: IFilterResult, index: number) => {
                              const onClickItem = () => onSelectedItem(group, item);

                              return (
                                <Fragment key={`${groupKey}-item-${index}`}>
                                  <ListItem sx={{ paddingTop: 0, paddingBottom: 0 }} onClick={onClickItem}>
                                    <ListItemIcon>
                                      <WidgetsIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary={`${item.title}: ${item.subTitle}`} secondary={item.content}/>
                                  </ListItem>
                                  <Divider variant="inset" component="li"/>
                                </Fragment>
                              );
                            })
                            }
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        }
      </Popper>

      {currentModule &&
        <QueryDialog request={currentModule.request} onDataArrived={currentModule.onDataArrived} onClosed={onClosed}/>}
    </div>
  );
};

export default SearchBoxComponent;
